import React, {useEffect, useState} from "react";
import {inject, observer} from 'mobx-react';
import {fetch} from "../../utils/api-wrapper";
import FilterInputField from "../../components/filter-input-field";
import PaginationControls from "../../components/pagination-controls";
import Select from "react-select";
import Checkbox from "../../components/checkbox";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faArrowDown, faArrowUp,
    faPencilAlt, faSpinner, faWindowClose
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../components/modal";
import {formatMatterNumber} from "../../utils/matter-display";
import HoverInfoCard from "../../components/hover-card";
import Switch from "react-switch";

const AdminMatters = ({data}) => {
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [filterValue, setFilterValue] = useState("");
    const [sorting, setSorting] = useState([{id: "number", desc: false}]);
    const [matters, setMatters] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalRowCount, setTotalRowCount] = useState(0);
    const [clients, setClients] = useState([]);
    const [client, setClient] = useState(null);
    const [showNewModal, setShowNewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteSelection, setDeleteSelection] = useState([]);
    const initialMatter = {number: '', description: '', matter_status: '', active: false, practice_group_timekeeper: null};
    const [newMatter, setNewMatter] = useState({...initialMatter, id_client: ''});
    const [editMatter, setEditMatter] = useState({...initialMatter, _id: null});
    const [users, setUsers] = useState([]);

    //pagination logic
    const indexOfFirstItem = (page - 1) * pageSize;
    const indexOfLastItem = indexOfFirstItem + matters.length - 1;
    const totalPages = Math.ceil(totalRowCount / pageSize);
    const emptyRowsCount = Math.max(0, pageSize - matters.length);
    const emptyRows = Array(emptyRowsCount).fill({});

    const add_delete_hidden = true

    useEffect(() => {
        setLoading(true);
        refreshClients()
    }, []);

    useEffect(() => {
        if (client) {
            setPage(1);
            refreshMatters(client);
        }

    }, [client, pageSize, sorting, filterValue]);

    useEffect(() => {
        if (client) {
            refreshMatters(client)
        }
    }, [page]);

    useEffect(() => {
        refreshUsers()
    }, [client]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (rows) => {
        setPage(1)
        setPageSize(rows)
    }

    // Common styles for react-select components.
    const styleSelect = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            border: "0 !important",
            boxShadow: state.isFocused ? "0px 0px 10px #F39800 " : "0 !important"
        })
    };

    const openNewModal = () => {
        setNewMatter({...initialMatter, id_client: client._id});
        setShowNewModal(true);
    }

    const selectClient = (client) => {
        setClient({...client})
        setPage(1)
        setFilterValue("");
        refreshMatters(client);
    }

    const refreshClients = () => {
        fetch("/clients/", "GET", {}, response => {
                const clients = response.data.clients;
                setClients(clients);

                setClient(clients.find(c => c._id === data.id_client)); //Set Client to the user's corresponding client
                setLoading(false);
            },
            error => {
                // Error handling
                setErrorMessage(data.t.read("failed_to_fetch_clients"));
                setLoading(false);
            }
        );
    }

    const refreshUsers = (selectedClient = client) => {
        const client_id = selectedClient?._id;
        fetch(`/client/${client_id}/usersDetail`, "GET", {}, response => {
            let fetchedUsers = response.data;
            setUsers([...fetchedUsers]);
        });
    }

    const refreshMatters = (selectClient = client) => {
        let start = (page - 1) * pageSize;
        const filters = [{id: "id_client", value: selectClient._id}];
        if (filterValue && filterValue.trim() !== "") {
            filters.push({id: "search", value: filterValue.trim()});
        }
        const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&sorting=${encodeURIComponent(JSON.stringify(sorting ?? []))}&start=${encodeURIComponent(start)}&page_size=${pageSize}`;
        fetch(`/matters/${searchData}`, "GET", {},
            response => {
                let fetchedDepartments = response.data.matters;
                setMatters([...fetchedDepartments]);
                setTotalRowCount(response.data.meta.totalRowCount)
                setLoading(false);
            });
    }

    // CRUD actions
    const openNew = () => {
        setNewMatter({...initialMatter, id_client: client._id});
        setShowNewModal(true);
    };

    const submitNew = e => {
        e.preventDefault();
        setShowNewModal(false);
        fetch('/matters/', 'POST', newMatter,
            () => {
                setSuccessMessage(data.t.read('matter_created'));
                refreshMatters();
            },
            () => {
                setErrorMessage(data.t.read('failed_to_create'));
            }
        );
    };

    const cancelNew = () => {
        setNewMatter({})
        setShowNewModal(false);
    };

    const openEdit = m => {
        setEditMatter(m);
        setShowEditModal(true);
    };

    const submitEdit = e => {
        e.preventDefault();
        setShowEditModal(false);
        updateMatter()
    };

    const updateMatter = (matter = {...editMatter}) => {

        const {_id, ...payload} = matter;
        fetch(`/matters/${_id.$oid}`, 'PUT', payload,
            () => {
                setSuccessMessage(data.t.read('matter_updated'));
                refreshMatters();
            },
            () => {
                setErrorMessage(data.t.read('failed_to_update'));
            }
        );
    }

    const cancelEdit = () => {
        setEditMatter({})
        setShowEditModal(false);
    };

    const confirmDelete = () => {
        fetch('/matters/', 'DELETE', {matter_ids: deleteSelection},
            () => {
                setSuccessMessage(data.t.read('matter_deleted'));
                refreshMatters();
                setDeleteSelection([]);
            },
            () => {
                setErrorMessage(data.t.read('failed_to_delete'));
            }
        );
        setShowDeleteModal(false);
    };


    const sortTable = (name) => {
        const sorter = sorting.find(s => s.id === name)
        if (sorter === undefined)
            return setSorting([{"id": name, "desc": true}])

        if (sorter.desc)
            return setSorting([{"id": name, "desc": false}])
        else
            return setSorting([])
    }

    const toggleSelect = id => setDeleteSelection(sel =>
        sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]
    );
    const selectAll = () => setDeleteSelection(
        deleteSelection.length === matters.length ? [] : matters.map(m => m._id.$oid)
    );
    const isAllSelected = () => matters.length && deleteSelection.length === matters.length;

    const userMap = (user) => {
        const id = user.id_user || user;
        const user_detail = users.find(u => u.id_user === id) || {};
        
        return {
            value: id,  // primitive
            label: `${user_detail.first_name} ${user_detail.last_name}`,
        };
    };
    
    const displayUserDetails = (matter) => {
        const user = users.find((user) => user.id_user === matter.user_responsible)
        return user ? userMap(user).label : null;
    }

    return !loading ?
                        <div>
                            <div className='pagination-row'>
                                <FilterInputField
                                    filterValue={filterValue} setFilterValue={setFilterValue} placeholder={""}>
                                </FilterInputField>
                                <PaginationControls
                                    page={page}
                                    totalPages={totalPages}
                                    indexOfFirstItem={indexOfFirstItem}
                                    indexOfLastItem={indexOfLastItem}
                                    handlePageChange={handlePageChange}
                                    totalItems={totalRowCount}
                                    data={data}
                                    handleRowsPerPageChange={handleRowsPerPageChange}
                                    rowsPerPage={pageSize}
                                ></PaginationControls>
                            </div>
                            <div className="d-flex justify-content-between align-items-center px-4 py-2">
                                <div className="flex mr-3">
                                    <Select
                                        name="client"
                                        isDisabled={data.role !== "SuperAdmin"}
                                        value={client === null ? null : {value: client, label: client.name}}
                                        options={clients.map(client => ({value: client, label: client.name}))}
                                        styles={styleSelect}
                                        classNamePrefix="select"
                                        onChange={(choice) => {
                                            selectClient(choice.value);
                                        }}
                                    />
                                </div>
                                {
                                    !add_delete_hidden &&
                                    <div className="d-flex">
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-sm ml-2"
                                            disabled={deleteSelection.length === 0}
                                            onClick={() => setShowDeleteModal(true)}>
                                            {data.t.read("delete") + " (" + deleteSelection.length + ")"}
                                        </button>
                                        <button
                                            className='btn btn-primary btn-sm ml-2 justify-content-end'
                                            type="button"
                                            onClick={openNewModal}>
                                            {data.t.read("add")}
                                        </button>
                                    </div>
                                }
                            </div>
                            <div className="mb-2 card rounded-lg admin-table">
                                <table className='table table-hover font-small'>
                                    <thead>
                                    <tr>
                                        {
                                            !add_delete_hidden &&
                                            <th>
                                                <Checkbox
                                                    disabled={!matters || !matters.length}
                                                    isChecked={isAllSelected()}
                                                    onChange={selectAll}
                                                />
                                            </th>
                                        }
                                        <th className="pointer col-2" onClick={() => sortTable("number")}>
                                            {data.t.read("matter.number")}
                                            {sorting.find(s => s.id === "number") &&
                                                <FontAwesomeIcon
                                                    className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                                    icon={sorting.find(s => s.id === "number").desc ? faArrowDown : faArrowUp}
                                                />
                                            }
                                        </th>
                                        <th className="pointer col-3" onClick={() => sortTable("description")}>
                                            {data.t.read("matter.description")}
                                            {sorting.find(s => s.id === "description") &&
                                                <FontAwesomeIcon
                                                    className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                                    icon={sorting.find(s => s.id === "description").desc ? faArrowDown : faArrowUp}
                                                />
                                            }
                                        </th>
                                        <th className="pointer col-3" onClick={() => sortTable("description")}>
                                            {data.t.read("matter.user_responsible")}
                                            {sorting.find(s => s.id === "description") &&
                                                <FontAwesomeIcon
                                                    className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                                    icon={sorting.find(s => s.id === "description").desc ? faArrowDown : faArrowUp}
                                                />
                                            }
                                        </th>
                                        <th className="pointer col-2" onClick={() => sortTable("created_at")}>
                                            {data.t.read("matter.created_at")}
                                            {sorting.find(s => s.id === "created_at") &&
                                                <FontAwesomeIcon
                                                    className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                                    icon={sorting.find(s => s.id === "created_at").desc ? faArrowDown : faArrowUp}
                                                />
                                            }
                                        </th>
                                        <th className="pointer col-2" onClick={() => sortTable("updated_at")}>
                                            {data.t.read("matter.updated_at")}
                                            {sorting.find(s => s.id === "updated_at") &&
                                                <FontAwesomeIcon
                                                    className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                                    icon={sorting.find(s => s.id === "updated_at").desc ? faArrowDown : faArrowUp}
                                                />
                                            }
                                        </th>
                                        <th className="pointer col-1 text-center"
                                            onClick={() => sortTable("active")}>
                                            {data.t.read("matter.active")}
                                            {sorting.find(s => s.id === "active") &&
                                                <FontAwesomeIcon
                                                    className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                                    icon={sorting.find(s => s.id === "active").desc ? faArrowDown : faArrowUp}
                                                />
                                            }
                                        </th>
                                        <th className="pointer">
                                            {"Actions"}
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {matters.map(matter => (
                                        <tr key={matter._id?.$oid}>
                                            {
                                                !add_delete_hidden &&
                                                <td>
                                                    <Checkbox
                                                        isChecked={deleteSelection.includes(matter._id.$oid)}
                                                        onChange={() =>
                                                            toggleSelect(matter._id.$oid)
                                                        }
                                                    />
                                                </td>
                                            }
                                            <td
                                                className="col-2 pointer"
                                                onClick={() => openEdit(matter)}
                                            >
                                                {/* XPEN-3322: the grid reads matters, so it shows the
                                                    collapsed number. The edit modal below keeps the stored
                                                    value — it authors the record. */}
                                                {formatMatterNumber(matter.number)}
                                            </td>
                                            <td
                                                className="col-3 pointer ml-3"
                                                onClick={() => openEdit(matter)}
                                            >
                                                <HoverInfoCard info={matter.description}/>
                                            </td>
                                            <td
                                                className="col-3 pointer ml-3"
                                                onClick={() => openEdit(matter)}
                                            >
                                                {displayUserDetails(matter)}
                                            </td>
                                            <td className="col-2 pointer"
                                                onClick={() => openEdit(matter)}
                                            >
                                                {matter.created_at ? Intl.DateTimeFormat(`${data.t.lang}-US`, {
                                                    year: 'numeric',
                                                    month: '2-digit',
                                                    day: '2-digit',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    second: '2-digit',
                                                    hour12: false,
                                                }).format(new Date(matter.created_at?.$date)) : ""}
                                            </td>
                                            <td className="col-2 pointer"
                                                onClick={() => openEdit(matter)}
                                            >
                                                {matter.updated_at ? Intl.DateTimeFormat(`${data.t.lang}-US`, {
                                                    year: 'numeric',
                                                    month: '2-digit',
                                                    day: '2-digit',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    second: '2-digit',
                                                    hour12: false,
                                                }).format(new Date(matter.updated_at?.$date)) : ""}
                                            </td>
                                            <td
                                                className="col-1 pointer"
                                            >
                                                <div className=" d-flex justify-content-center align-items-center">
                                                    <Switch
                                                        className='switch mt-0  '
                                                        checked={matter.active}
                                                        width={70}
                                                        onColor="#F39800"
                                                        onHandleColor="#2693e6"
                                                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                                        activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                                                        onChange={(active) => updateMatter({...matter, active: active})}
                                                    />
                                                </div>
                                            </td>
                                            <td>
                                                <div className={"d-flex justify-content-center"}>
                                                    <FontAwesomeIcon
                                                        icon={faPencilAlt}
                                                        onClick={() => openEdit(matter)}
                                                        type="button"
                                                    />
                                                    {
                                                        !add_delete_hidden &&
                                                        <FontAwesomeIcon
                                                            className={"ml-2"}
                                                            icon={faWindowClose}
                                                            type="button"
                                                            style={{color: 'red'}}
                                                            onClick={() => {
                                                                setDeleteSelection([matter._id?.$oid]);
                                                                setShowDeleteModal(true);
                                                            }}
                                                        />
                                                    }
                                                </div>


                                            </td>
                                        </tr>
                                    ))}

                                    {emptyRows.map((_, idx) => (
                                        <tr key={`empty-${idx}`}>
                                            <td colSpan="6">&nbsp;</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                                {
                                    showEditModal &&
                                    <MatterModal title={data.t.read("matter.edit_matter")} data={data}
                                                 matter={editMatter} setMatter={setEditMatter} handleSubmit={submitEdit}
                                                 handleCancel={cancelEdit}
                                                 users={users}
                                                 userMap={userMap}/>
                                }
                                {
                                    showNewModal &&
                                    <MatterModal title={data.t.read("matter.new_matter")} data={data} matter={newMatter}
                                                 setMatter={setNewMatter} handleSubmit={submitNew}
                                                 handleCancel={cancelNew}
                                                 users={users}
                                                 userMap={userMap}/>
                                }
                            </div>
                        </div>

                        :
                        <div>
                            <div className="d-flex loading-container w-100">
                                <FontAwesomeIcon className="infinite-rotate" icon={faSpinner}/>
                            </div>
                        </div>
};


export const MatterModal = ({title = "", data, users = [], userMap, matter, setMatter, handleSubmit, handleCancel}) => {
    const userStyleSelect = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            boxShadow: "none",
            backgroundColor: "transparent",
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
    };
    
    return (
        <Modal title={title}>
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="number">{data.t.read("matter.number")}</label>
                    <input
                        type="text"
                        className="form-control"
                        name="number"
                        value={matter.number}
                        onChange={e =>
                            setMatter({
                                ...matter,
                                number: e.target.value
                            })
                        }
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="description">{data.t.read("matter.description")}</label>
                    <textarea
                        className="form-control"
                        name="description"
                        rows={3}
                        value={matter.description}
                        onChange={e =>
                            setMatter({
                                ...matter,
                                description: e.target.value
                            })
                        }
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="matter_status">{data.t.read("matter.user_responsible")}</label>
                    <Select
                        menuPortalTarget={document.body}

                        styles={userStyleSelect}
                        classNamePrefix="select"
                        name="user_responsible"
                        value={
                            users.filter((user) => user.id_user === matter.user_responsible).map(userMap) ?? null
                        }
                        options={[
                            {label:data.t.read("matter.none"), value:null},...users.map(userMap)]}
                        onChange={choice => {
                            setMatter({
                                ...matter,
                                user_responsible: choice.value
                            })
                        }
                        }
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="practice_group_timekeeper">{data.t.read("matter.practice_group_timekeeper")}</label>
                    <Select
                        menuPortalTarget={document.body}
                        styles={userStyleSelect}
                        classNamePrefix="select"
                        name="practice_group_timekeeper"
                        value={
                            users.filter((user) => user.id_user === matter.practice_group_timekeeper).map(userMap) ?? null
                        }
                        options={[
                            {label:data.t.read("matter.none"), value:null},...users.map(userMap)]}
                        onChange={choice => {
                            setMatter({
                                ...matter,
                                practice_group_timekeeper: choice.value
                            })
                        }
                        }
                    />
                </div>

                <div className="form-group align-items-center">
                    <label className="form-check-label" htmlFor="active">
                        {data.t.read("matter.active")}
                    </label>
                    <Switch
                        className='switch d-block mt-1 ml-2'
                        checked={matter.active}
                        width={70}
                        onColor="#F39800"
                        onHandleColor="#2693e6"
                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                        activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                        onChange={(active) => setMatter({
                            ...matter,
                            active: active
                        })}
                    />


                </div>

                <span className="d-flex justify-content-between">
        <button
            type="button"
            className="btn btn-outline-dark"
            onClick={handleCancel}
        >
          {data.t.read("cancel")}
        </button>
        <button
            type="submit"
            className="btn btn-primary"
        >
          {data.t.read("save")}
        </button>
      </span>
            </form>
        </Modal>
    )
}
export default inject('data')(observer(AdminMatters));
